import {
	INDOOR_AIR_GAP_M,
	INDOOR_FIBREBOARD_T_M,
	INDOOR_WALLS,
	type IndoorRoomConfig,
	type IndoorWall,
} from "../../domain/indoor/indoorRoom";
import type { SaunaDimensions } from "../../engines/types";

/**
 * How far the room reaches past a sauna wall that stands free in it. Enough
 * floor to read as "a room around the sauna", little enough that the camera
 * presets still frame the sauna.
 */
export const INDOOR_ROOM_FREE_CLEARANCE_M = 1.2;

export interface IndoorRoomWallPlane {
	wall: IndoorWall;
	/** Centre of the wall's inner (room-facing) face, MODEL frame. */
	position: [number, number, number];
	/** Y rotation that turns a +Z-facing plane to face the sauna. */
	rotationY: number;
	width: number;
	height: number;
}

export interface IndoorRoomLayout {
	/** Inner faces of the room, MODEL frame: the extent of floor to draw. */
	minX: number;
	maxX: number;
	minZ: number;
	maxZ: number;
	walls: IndoorRoomWallPlane[];
}

/**
 * Issue 175: the room an indoor sauna is built into, in the sauna's MODEL
 * frame. A wall the sauna stands against is drawn at the sauna's outer face +
 * wood-fibre board + air gap — exactly where `buildIndoorOuterLayers` leaves
 * room for it — and the room reaches `INDOOR_ROOM_FREE_CLEARANCE_M` past
 * every free wall.
 */
export function resolveIndoorRoomLayout(
	dimensions: SaunaDimensions,
	room: IndoorRoomConfig,
	roomHeightM: number,
): IndoorRoomLayout {
	const halfW = dimensions.width / 2;
	const halfL = dimensions.length / 2;
	const against = INDOOR_FIBREBOARD_T_M + INDOOR_AIR_GAP_M;
	const reach = (wall: IndoorWall) =>
		room.existingWalls.includes(wall) ? against : INDOOR_ROOM_FREE_CLEARANCE_M;
	const minX = -halfW - reach("left");
	const maxX = halfW + reach("right");
	const minZ = -halfL - reach("back");
	const maxZ = halfL + reach("front");
	const y = roomHeightM / 2;
	const spanX = maxX - minX;
	const spanZ = maxZ - minZ;
	const midX = (minX + maxX) / 2;
	const midZ = (minZ + maxZ) / 2;
	const planes: Record<IndoorWall, IndoorRoomWallPlane> = {
		front: {
			wall: "front",
			position: [midX, y, maxZ],
			rotationY: Math.PI,
			width: spanX,
			height: roomHeightM,
		},
		back: {
			wall: "back",
			position: [midX, y, minZ],
			rotationY: 0,
			width: spanX,
			height: roomHeightM,
		},
		left: {
			wall: "left",
			position: [minX, y, midZ],
			rotationY: Math.PI / 2,
			width: spanZ,
			height: roomHeightM,
		},
		right: {
			wall: "right",
			position: [maxX, y, midZ],
			rotationY: -Math.PI / 2,
			width: spanZ,
			height: roomHeightM,
		},
	};
	return {
		minX,
		maxX,
		minZ,
		maxZ,
		walls: INDOOR_WALLS.filter((wall) => room.existingWalls.includes(wall)).map(
			(wall) => planes[wall],
		),
	};
}
